import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getCachedTierInfo } from './services/tierService';
import { usePageView } from './services/telemetryService';
import AuthBootLoader from './components/AuthBootLoader';
import LandingPage from './pages/LandingPage';
// Dashboard retired — /dashboard redirects to /command-center
import FastScan from './pages/FastScan';
import ComprehensiveScan from './pages/ComprehensiveScan';
import Reports from './pages/Reports';
import Schedules from './pages/Schedules';
import Assets from './pages/Assets';
import AssetDetails from './pages/AssetDetails';
import Upgrade from './pages/Upgrade';
import ThankYou from './pages/ThankYou';
import Validate from './pages/Validate';
import Tools from './pages/Tools';
import Settings from './pages/Settings';
import CommandCenter from './pages/CommandCenter';
import Inventory from './pages/Inventory';
import Suppressions from './pages/Suppressions';
import DriftAnalytics from './pages/DriftAnalytics';
import DriftAlarms from './pages/DriftAlarms';
import DriftRules from './pages/DriftRules';
import HowItWorks from './pages/HowItWorks';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import AcceptableUse from './pages/AcceptableUse';
import DataProcessingAgreement from './pages/DataProcessingAgreement';
import AttackSurfaceMonitoringGuide from './pages/guides/AttackSurfaceMonitoringGuide';
import ExposedRDPGuide from './pages/guides/ExposedRDPGuide';
import ExposedSSHGuide from './pages/guides/ExposedSSHGuide';
import CloudStorageExposureGuide from './pages/guides/CloudStorageExposureGuide';
import EOLSoftwareGuide from './pages/guides/EOLSoftwareGuide';
import PHP2ShellGuide from './pages/guides/PHP2ShellGuide';
import ExposedAdminPanelsGuide from './pages/guides/ExposedAdminPanelsGuide';
import VulnerabilityValidationGuide from './pages/guides/VulnerabilityValidationGuide';
import ConfigurationDriftGuide from './pages/guides/ConfigurationDriftGuide';
import DNSDriftGuide from './pages/guides/DNSDriftGuide';
import TLSSSLDriftGuide from './pages/guides/TLSSSLDriftGuide';
import EASMvsVulnScanGuide from './pages/guides/EASMvsVulnScanGuide';
import AttackSurfaceChecklistGuide from './pages/guides/AttackSurfaceChecklistGuide';
import ManualReconvsEASMGuide from './pages/guides/ManualReconvsEASMGuide';
import EASMComplianceGuide from './pages/guides/EASMComplianceGuide';
import ExposedDatabasePortsGuide from './pages/guides/ExposedDatabasePortsGuide';
import AIVulnerabilityThreatsGuide from './pages/guides/AIVulnerabilityThreatsGuide';
import PatchWindowCollapseGuide from './pages/guides/PatchWindowCollapseGuide';
import AIAttackReadinessGuide from './pages/guides/AIAttackReadinessGuide';
import WAFVulnerabilityScanningGuide from './pages/guides/WAFVulnerabilityScanningGuide';
import GuidesIndex from './pages/guides/GuidesIndex';
import {
  AttackSurfaceMonitoringPage,
  ExternalAttackSurfaceManagementPage,
  DomainMonitoringPage,
  SubdomainMonitoringPage,
  IpRangeMonitoringPage,
  DnsDriftMonitoringPage,
  SslCertificateMonitoringPage,
  ExposedRdpMonitoringPage,
  ExposedSshMonitoringPage,
  ExternalVulnerabilityScanningPage,
} from './pages/landing';
import ScanResults from './components/ScanResults';
import ValidateResults from './components/ValidateResults';
import Navbar from './components/Navbar';
import ScrutinizeResults from './components/Scrutinize/ScrutinizeResults';
import WeeklyReportDetail from './pages/WeeklyReportDetail';
import { NavProvider } from './contexts/NavContext';

// UX Overhaul Prototypes
import DashboardPrototypes from './pages/prototypes/DashboardPrototypes';
import DashboardMissionControl from './pages/prototypes/DashboardMissionControl';
import DashboardIntelBrief from './pages/prototypes/DashboardIntelBrief';
import DashboardThreatMap from './pages/prototypes/DashboardThreatMap';
import DashboardAnalyst from './pages/prototypes/DashboardAnalyst';
import DashboardCleanModern from './pages/prototypes/DashboardCleanModern';

// Asset Dossier Prototypes
import AssetDossierClassic from './pages/prototypes/AssetDossierClassic';
import AssetDossierScroll from './pages/prototypes/AssetDossierScroll';
import AssetDossierGrid from './pages/prototypes/AssetDossierGrid';
import AssetDetailsRedux from './pages/prototypes/AssetDetailsRedux';
import AssetDetailsReduxV2 from './pages/prototypes/AssetDetailsReduxV2';
// Note: V3 prototype was promoted to AssetDetails.js (production) on 2026-04-11
// per PRD-43 Session 3. The old /asset-details-redux-v3 route was removed.
// Previous production AssetDetails.js is preserved as AssetDetails_legacy.js
// for rollback.

// PRD-132A WP6: /proto/<family>-NN prototype routes are React.lazy code-split so 132B–E's
// 40 future prototype components don't bloat the main bundle. 132B–E append a lazy import
// here + one <Route> below following this exact pattern.
const LandingProto00 = lazy(() => import('./pages/prototypes/landing/LandingProto00'));
// PRD-132B Batch A (Landing, enhance)
const LandingAuroraHero = lazy(() => import('./pages/prototypes/landing/LandingAuroraHero'));
const LandingSpotlightFeatures = lazy(() => import('./pages/prototypes/landing/LandingSpotlightFeatures'));
const LandingThreatTicker = lazy(() => import('./pages/prototypes/landing/LandingThreatTicker'));
const LandingNeonGrid = lazy(() => import('./pages/prototypes/landing/LandingNeonGrid'));
const LandingShimmerLoad = lazy(() => import('./pages/prototypes/landing/LandingShimmerLoad'));
// PRD-132B Batch B (Landing, enhance L6 + rethink L7–L10)
const LandingBentoProof = lazy(() => import('./pages/prototypes/landing/LandingBentoProof'));
const LandingScanFirst = lazy(() => import('./pages/prototypes/landing/LandingScanFirst'));
const LandingAttackSurface = lazy(() => import('./pages/prototypes/landing/LandingAttackSurface'));
const LandingScrollytelling = lazy(() => import('./pages/prototypes/landing/LandingScrollytelling'));
const LandingTerminalScan = lazy(() => import('./pages/prototypes/landing/LandingTerminalScan'));
// PRD-132B Batch C (Landing, education-first reactbits additions)
const LandingIridescenceHero = lazy(() => import('./pages/prototypes/landing/LandingIridescenceHero'));
const LandingDriftLifecycle = lazy(() => import('./pages/prototypes/landing/LandingDriftLifecycle'));
const LandingWhatWeMonitor = lazy(() => import('./pages/prototypes/landing/LandingWhatWeMonitor'));
const LandingDecryptExposure = lazy(() => import('./pages/prototypes/landing/LandingDecryptExposure'));
// PRD-132C Batch 1 (Command Center, enhance)
const Cc01GlowMetrics = lazy(() => import('./pages/prototypes/cc/Cc01GlowMetrics'));
const Cc02AuroraBento = lazy(() => import('./pages/prototypes/cc/Cc02AuroraBento'));
const Cc03DriftMarquee = lazy(() => import('./pages/prototypes/cc/Cc03DriftMarquee'));
// PRD-132C Batch 2 (Command Center, enhance)
const Cc04GradientKpi = lazy(() => import('./pages/prototypes/cc/Cc04GradientKpi'));
const Cc05ShimmerChoreo = lazy(() => import('./pages/prototypes/cc/Cc05ShimmerChoreo'));
const Cc06TiltGlowAssets = lazy(() => import('./pages/prototypes/cc/Cc06TiltGlowAssets'));
// PRD-132C Batch 3 (Command Center, rethink)
const Cc07TriageWorklist = lazy(() => import('./pages/prototypes/cc/Cc07TriageWorklist'));
const Cc08MapCentric = lazy(() => import('./pages/prototypes/cc/Cc08MapCentric'));
const Cc09TimelineFirst = lazy(() => import('./pages/prototypes/cc/Cc09TimelineFirst'));
const Cc10FocusMode = lazy(() => import('./pages/prototypes/cc/Cc10FocusMode'));
// PRD-132D Batch 1 (Asset Details, enhance)
const AssetProto01GlowDossier = lazy(() => import('./pages/prototypes/asset/AssetProto01_GlowDossier'));
const AssetProto02AuroraProfile = lazy(() => import('./pages/prototypes/asset/AssetProto02_AuroraProfile'));
const AssetProto03GradientSeverity = lazy(() => import('./pages/prototypes/asset/AssetProto03_GradientSeverity'));
// PRD-132D Batch 2 (Asset Details, enhance)
const AssetProto04BentoAsset = lazy(() => import('./pages/prototypes/asset/AssetProto04_BentoAsset'));
const AssetProto05RevealTabs = lazy(() => import('./pages/prototypes/asset/AssetProto05_RevealTabs'));
const AssetProto06ShimmerDetail = lazy(() => import('./pages/prototypes/asset/AssetProto06_ShimmerDetail'));
// PRD-132D Batch 3 (Asset Details, rethink)
const AssetProto07StoryOfTheAsset = lazy(() => import('./pages/prototypes/asset/AssetProto07_StoryOfTheAsset'));
const AssetProto08RiskFirstTriage = lazy(() => import('./pages/prototypes/asset/AssetProto08_RiskFirstTriage'));
// PRD-132D Batch 4 (Asset Details, rethink)
const AssetProto09AttackPathGraph = lazy(() => import('./pages/prototypes/asset/AssetProto09_AttackPathGraph'));
const AssetProto10BeforeAfterDiff = lazy(() => import('./pages/prototypes/asset/AssetProto10_BeforeAfterDiff'));
// PRD-132E Batch 1 (Fast Scan Results, enhance)
const ScanGlowFindings = lazy(() => import('./pages/prototypes/scan/ScanGlowFindings'));
const ScanAuroraBrief = lazy(() => import('./pages/prototypes/scan/ScanAuroraBrief'));
const ScanCounterSummary = lazy(() => import('./pages/prototypes/scan/ScanCounterSummary'));
// PRD-132E Batch 2 (Fast Scan Results, enhance)
const ScanBentoSurface = lazy(() => import('./pages/prototypes/scan/ScanBentoSurface'));
const ScanRevealCascade = lazy(() => import('./pages/prototypes/scan/ScanRevealCascade'));
const ScanShimmerStream = lazy(() => import('./pages/prototypes/scan/ScanShimmerStream'));
// PRD-132E Batch 3 (Fast Scan Results, rethink)
const ScanStoryScroll = lazy(() => import('./pages/prototypes/scan/ScanStoryScroll'));
const ScanTriageWorklist = lazy(() => import('./pages/prototypes/scan/ScanTriageWorklist'));
// PRD-132E Batch 4 (Fast Scan Results, rethink)
const ScanExecOnePager = lazy(() => import('./pages/prototypes/scan/ScanExecOnePager'));
const ScanConversionFunnel = lazy(() => import('./pages/prototypes/scan/ScanConversionFunnel'));

// Wrapper component to get jobId from URL params for ValidateResults
function ValidateResultsPage() {
  const { jobId } = useParams();
  return <ValidateResults jobId={jobId} />;
}

// Wrapper component to handle post-login redirect
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  // PRD-151 WP2: first-party page_view + GA4 page_view on every route change.
  // No-ops when unauthenticated or during react-snap pre-render; route is
  // normalized (query/hash stripped, id segments templated) inside the hook.
  usePageView();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const isDevelopment = process.env.NODE_ENV === 'development';
  const [tierInfo, setTierInfo] = useState(null);

  // AUTHENTICATION ENABLED
  // Users must sign in to access protected routes
  const AUTH_ENABLED = true;

  // In development OR when auth is disabled, allow access
  // In production with auth enabled, require authentication
  const isAuthorized = isDevelopment || !AUTH_ENABLED || isAuthenticated;

  // PRD-79: useIsAuthenticated() returns false while MsalProvider's internal
  // inProgress state is still 'startup', even when the cached account is
  // already loaded from localStorage. Without this gate, opening a protected
  // route in a new tab fires <Navigate to="/" replace /> ~100ms before MSAL
  // finishes hydrating, permanently redirecting the user to the landing page.
  const isAuthBooting = AUTH_ENABLED
    && !isDevelopment
    && inProgress !== InteractionStatus.None;

  // Public pages that don't require auth and have their own navigation
  const publicPagesWithOwnNav = ['/', '/how-it-works', '/upgrade', '/thank-you', '/terms', '/privacy', '/acceptable-use', '/dpa', '/guides', '/guides/attack-surface-monitoring', '/guides/exposed-rdp', '/guides/exposed-ssh', '/guides/cloud-storage-exposure', '/guides/eol-software', '/guides/exposed-admin-panels', '/guides/vulnerability-validation', '/guides/configuration-drift', '/guides/dns-drift-detection', '/guides/tls-ssl-drift', '/guides/easm-vs-vulnerability-scanning', '/guides/attack-surface-checklist', '/guides/manual-recon-vs-easm', '/guides/easm-compliance', '/guides/exposed-database-ports', '/guides/ai-vulnerability-threats', '/guides/patch-window-collapse', '/guides/ai-attack-readiness', '/guides/waf-and-vulnerability-scanning', '/guides/php2shell-cve-2026-109618-109619', '/attack-surface-monitoring', '/external-attack-surface-management', '/domain-monitoring', '/subdomain-monitoring', '/ip-range-monitoring', '/dns-drift-monitoring', '/ssl-certificate-monitoring', '/exposed-rdp-monitoring', '/exposed-ssh-monitoring', '/external-vulnerability-scanning'];
  const isPublicPageWithOwnNav = publicPagesWithOwnNav.includes(location.pathname);

  // Fetch tier info for trial expiry checks
  useEffect(() => {
    if (isAuthenticated) {
      getCachedTierInfo().then(info => setTierInfo(info)).catch(() => {});
    }
  }, [isAuthenticated]);

  // Trial expiry gate: redirect expired trial users to /upgrade
  // Allow access to: /upgrade, /settings, public pages
  const isTrialExpired = tierInfo?.isTrialExpired === true;
  const exemptFromTrialGate = ['/upgrade', '/settings'];
  const shouldBlockForExpiredTrial = isTrialExpired
    && !isPublicPageWithOwnNav
    && !exemptFromTrialGate.includes(location.pathname);

  // Handle post-login redirect
  useEffect(() => {
    if (isAuthenticated) {
      const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
      if (postLoginRedirect) {
        sessionStorage.removeItem('postLoginRedirect');
        navigate(postLoginRedirect);
      }
    }
  }, [isAuthenticated, navigate]);

  // Apply saved theme on app load (default: arctic-frost)
  useEffect(() => {
    const savedTheme = localStorage.getItem('driftalarm-theme') || 'arctic-frost';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Wrapper to force AssetDetails remount when assetId changes (prevents stale state)
  function AssetDetailsKeyed() {
    const { assetId } = useParams();
    return <AssetDetails key={assetId} />;
  }

  // PRD-79: shared route-guard helper. Replaces 30 inline ternary guards.
  // skipTrialGate=true preserves the pre-PRD behavior of Pattern B routes
  // (/settings + prototypes), which never enforced the trial-expired bounce.
  const protectedElement = (Component, { skipTrialGate = false } = {}) => {
    if (isAuthBooting) return <AuthBootLoader />;
    if (!isAuthorized) {
      // Preserve deep-link destination so post-CIAM redirect can bounce back.
      sessionStorage.setItem(
        'postLoginRedirect',
        location.pathname + location.search + location.hash
      );
      return <Navigate to="/" replace />;
    }
    if (!skipTrialGate && shouldBlockForExpiredTrial) {
      return <Navigate to="/upgrade" state={{ reason: 'trial_expired' }} replace />;
    }
    return Component;
  };

  return (
    <div className="app-container">
      {isAuthorized && !isPublicPageWithOwnNav && <Navbar />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/upgrade" element={<Upgrade />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/dpa" element={<DataProcessingAgreement />} />
        <Route path="/guides" element={<GuidesIndex />} />
        <Route path="/guides/attack-surface-monitoring" element={<AttackSurfaceMonitoringGuide />} />
        <Route path="/guides/exposed-rdp" element={<ExposedRDPGuide />} />
        <Route path="/guides/exposed-ssh" element={<ExposedSSHGuide />} />
        <Route path="/guides/cloud-storage-exposure" element={<CloudStorageExposureGuide />} />
        <Route path="/guides/eol-software" element={<EOLSoftwareGuide />} />
        <Route path="/guides/php2shell-cve-2026-109618-109619" element={<PHP2ShellGuide />} />
        <Route path="/guides/exposed-admin-panels" element={<ExposedAdminPanelsGuide />} />
        <Route path="/guides/vulnerability-validation" element={<VulnerabilityValidationGuide />} />
        {/* SEO-04 Guides Program */}
        <Route path="/guides/configuration-drift" element={<ConfigurationDriftGuide />} />
        <Route path="/guides/dns-drift-detection" element={<DNSDriftGuide />} />
        <Route path="/guides/tls-ssl-drift" element={<TLSSSLDriftGuide />} />
        <Route path="/guides/easm-vs-vulnerability-scanning" element={<EASMvsVulnScanGuide />} />
        <Route path="/guides/attack-surface-checklist" element={<AttackSurfaceChecklistGuide />} />
        <Route path="/guides/manual-recon-vs-easm" element={<ManualReconvsEASMGuide />} />
        <Route path="/guides/easm-compliance" element={<EASMComplianceGuide />} />
        <Route path="/guides/exposed-database-ports" element={<ExposedDatabasePortsGuide />} />
        <Route path="/guides/ai-vulnerability-threats" element={<AIVulnerabilityThreatsGuide />} />
        <Route path="/guides/patch-window-collapse" element={<PatchWindowCollapseGuide />} />
        <Route path="/guides/ai-attack-readiness" element={<AIAttackReadinessGuide />} />
        <Route path="/guides/waf-and-vulnerability-scanning" element={<WAFVulnerabilityScanningGuide />} />

        {/* SEO Landing Pages (SEO-03) */}
        <Route path="/attack-surface-monitoring" element={<AttackSurfaceMonitoringPage />} />
        <Route path="/external-attack-surface-management" element={<ExternalAttackSurfaceManagementPage />} />
        <Route path="/domain-monitoring" element={<DomainMonitoringPage />} />
        <Route path="/subdomain-monitoring" element={<SubdomainMonitoringPage />} />
        <Route path="/ip-range-monitoring" element={<IpRangeMonitoringPage />} />
        <Route path="/dns-drift-monitoring" element={<DnsDriftMonitoringPage />} />
        <Route path="/ssl-certificate-monitoring" element={<SslCertificateMonitoringPage />} />
        <Route path="/exposed-rdp-monitoring" element={<ExposedRdpMonitoringPage />} />
        <Route path="/exposed-ssh-monitoring" element={<ExposedSshMonitoringPage />} />
        <Route path="/external-vulnerability-scanning" element={<ExternalVulnerabilityScanningPage />} />

        {/* Protected Routes — trial-expired users get redirected to /upgrade */}
        <Route
          path="/dashboard"
          element={<Navigate to="/command-center" replace />}
        />
        <Route path="/command-center" element={protectedElement(<CommandCenter />)} />
        <Route path="/fast-scan" element={protectedElement(<FastScan />)} />
        <Route path="/scrutinize-result/:scanId" element={protectedElement(<ScrutinizeResults />)} />
        <Route path="/scan-result/:scanId" element={protectedElement(<ScanResults />)} />
        <Route path="/comprehensive-scan" element={protectedElement(<ComprehensiveScan />)} />
        <Route path="/reports" element={protectedElement(<Reports />)} />
        <Route path="/weekly-report/:reportId" element={protectedElement(<WeeklyReportDetail />)} />
        <Route path="/schedules" element={protectedElement(<Schedules />)} />
        <Route path="/assets" element={protectedElement(<Assets />)} />
        <Route path="/assets/:assetId" element={protectedElement(<AssetDetailsKeyed />)} />
        <Route path="/inventory" element={protectedElement(<Inventory />)} />
        <Route path="/validate" element={protectedElement(<Validate />)} />
        <Route path="/validate/results/:jobId" element={protectedElement(<ValidateResultsPage />)} />
        <Route path="/tools" element={protectedElement(<Tools />)} />
        <Route path="/settings" element={protectedElement(<Settings />, { skipTrialGate: true })} />
        <Route path="/suppressions" element={protectedElement(<Suppressions />)} />
        <Route path="/drift-alarms" element={protectedElement(<DriftAlarms />)} />
        <Route path="/drift-analytics" element={protectedElement(<DriftAnalytics />)} />
        <Route path="/drift-rules" element={protectedElement(<DriftRules />)} />

        {/* UX Overhaul Prototypes — skipTrialGate retains pre-PRD-79 behavior */}
        <Route path="/dashboard-prototypes" element={protectedElement(<DashboardPrototypes />, { skipTrialGate: true })} />
        <Route path="/dashboard-v1" element={protectedElement(<DashboardMissionControl />, { skipTrialGate: true })} />
        <Route path="/dashboard-v2" element={protectedElement(<DashboardIntelBrief />, { skipTrialGate: true })} />
        <Route path="/dashboard-v3" element={protectedElement(<DashboardThreatMap />, { skipTrialGate: true })} />
        <Route path="/dashboard-v4" element={protectedElement(<DashboardAnalyst />, { skipTrialGate: true })} />
        <Route path="/dashboard-v5" element={protectedElement(<DashboardCleanModern />, { skipTrialGate: true })} />

        {/* Asset Dossier Prototypes */}
        <Route path="/asset-details-v1/:assetId" element={protectedElement(<AssetDossierClassic />, { skipTrialGate: true })} />
        <Route path="/asset-details-v2/:assetId" element={protectedElement(<AssetDossierScroll />, { skipTrialGate: true })} />
        <Route path="/asset-details-v3/:assetId" element={protectedElement(<AssetDossierGrid />, { skipTrialGate: true })} />
        <Route path="/asset-details-redux/:assetId" element={protectedElement(<AssetDetailsRedux />, { skipTrialGate: true })} />
        <Route path="/asset-details-redux-v2/:assetId" element={protectedElement(<AssetDetailsReduxV2 />, { skipTrialGate: true })} />

        {/* PRD-132A /proto/<family>-NN convention — lazy-loaded, protected + skipTrialGate.
            Suspense is scoped to each lazy proto element (not the whole Routes) so the rest
            of the app's static imports are unaffected. 132B–E append routes here. */}
        <Route path="/proto/landing-00" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingProto00 />, { skipTrialGate: true })}
          </Suspense>
        } />
        {/* PRD-132B Batch A (Landing, enhance) */}
        <Route path="/proto/landing-01" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingAuroraHero />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-02" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingSpotlightFeatures />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-03" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingThreatTicker />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-04" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingNeonGrid />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-05" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingShimmerLoad />, { skipTrialGate: true })}
          </Suspense>
        } />
        {/* PRD-132B Batch B (Landing, enhance L6 + rethink L7–L10) */}
        <Route path="/proto/landing-06" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingBentoProof />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-07" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingScanFirst />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-08" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingAttackSurface />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-09" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingScrollytelling />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-10" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingTerminalScan />, { skipTrialGate: true })}
          </Suspense>
        } />
        {/* PRD-132B Batch C (Landing, education-first reactbits additions) */}
        <Route path="/proto/landing-11" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingIridescenceHero />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-12" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingDriftLifecycle />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-13" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingWhatWeMonitor />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/landing-14" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<LandingDecryptExposure />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132C Batch 1 (Command Center, enhance) */}
        <Route path="/proto/cc-01" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc01GlowMetrics />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-02" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc02AuroraBento />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-03" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc03DriftMarquee />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132C Batch 2 (Command Center, enhance) */}
        <Route path="/proto/cc-04" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc04GradientKpi />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-05" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc05ShimmerChoreo />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-06" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc06TiltGlowAssets />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132C Batch 3 (Command Center, rethink) */}
        <Route path="/proto/cc-07" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc07TriageWorklist />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-08" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc08MapCentric />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-09" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc09TimelineFirst />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/cc-10" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<Cc10FocusMode />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132D Batch 1 (Asset Details, enhance) */}
        <Route path="/proto/asset-01" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto01GlowDossier />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-02" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto02AuroraProfile />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-03" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto03GradientSeverity />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132D Batch 2 (Asset Details, enhance) */}
        <Route path="/proto/asset-04" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto04BentoAsset />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-05" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto05RevealTabs />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-06" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto06ShimmerDetail />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132D Batch 3 (Asset Details, rethink) */}
        <Route path="/proto/asset-07" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto07StoryOfTheAsset />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-08" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto08RiskFirstTriage />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-09" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto09AttackPathGraph />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/asset-10" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<AssetProto10BeforeAfterDiff />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132E Batch 1 (Fast Scan Results, enhance) */}
        <Route path="/proto/scan-01" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanGlowFindings />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/scan-02" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanAuroraBrief />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/scan-03" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanCounterSummary />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132E Batch 2 (Fast Scan Results, enhance) */}
        <Route path="/proto/scan-04" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanBentoSurface />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/scan-05" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanRevealCascade />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/scan-06" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanShimmerStream />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132E Batch 3 (Fast Scan Results, rethink) */}
        <Route path="/proto/scan-07" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanStoryScroll />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/scan-08" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanTriageWorklist />, { skipTrialGate: true })}
          </Suspense>
        } />

        {/* PRD-132E Batch 4 (Fast Scan Results, rethink) */}
        <Route path="/proto/scan-09" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanExecOnePager />, { skipTrialGate: true })}
          </Suspense>
        } />
        <Route path="/proto/scan-10" element={
          <Suspense fallback={<div style={{ padding: 24, fontFamily: 'monospace' }}>Loading prototype…</div>}>
            {protectedElement(<ScanConversionFunnel />, { skipTrialGate: true })}
          </Suspense>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <NavProvider>
        <AppContent />
      </NavProvider>
    </Router>
  );
}

export default App;